export interface CanvasElement {
  id: string;
  type: string;
  properties: Record<string, unknown>;
}

interface PhoneCanvasProps {
  elements: CanvasElement[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onDrop: (e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent) => void;
  onReorder: (fromIndex: number, toIndex: number) => void;
  appSettings: { primaryColor: string; backgroundColor: string; fontFamily: string };
  device: 'ios' | 'android';
  previewMode?: boolean;
}

function renderElement(
  el: CanvasElement,
  isSelected: boolean,
  onSelect: (id: string | null) => void,
  onDelete: (id: string) => void,
  onReorderStart: (index: number) => void,
  onReorderOver: (index: number) => void,
  onReorderEnd: () => void,
  index: number,
  previewMode: boolean
) {
  const p = el.properties;

  const wrapper = (content: React.ReactNode) => (
    <div
      key={el.id}
      draggable={!previewMode}
      onDragStart={() => onReorderStart(index)}
      onDragOver={(e) => { e.preventDefault(); onReorderOver(index); }}
      onDragEnd={onReorderEnd}
      onClick={(e) => { e.stopPropagation(); if (!previewMode) onSelect(el.id); }}
      className={`relative transition-all ${!previewMode ? 'cursor-move' : ''} ${
        isSelected && !previewMode
          ? 'ring-2 ring-teal-500 ring-offset-1 rounded-lg'
          : !previewMode ? 'hover:ring-1 hover:ring-teal-300 hover:ring-offset-1 rounded-lg' : ''
      }`}
    >
      {content}
      {isSelected && !previewMode && (
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(el.id); }}
          className="absolute -top-2.5 -right-2.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors shadow-md cursor-pointer z-10"
        >
          <i className="ri-close-line text-xs"></i>
        </button>
      )}
    </div>
  );

  switch (el.type) {
    case 'button':
      return wrapper(
        <button
          style={{
            backgroundColor: p.backgroundColor as string,
            color: p.textColor as string,
            borderRadius: `${p.borderRadius}px`,
            width: p.fullWidth ? '100%' : 'auto',
          }}
          className="px-5 py-3 font-semibold text-sm pointer-events-none"
        >
          {p.text as string}
        </button>
      );
    case 'text':
      return wrapper(
        <p style={{ fontSize: `${p.fontSize}px`, color: p.color as string, fontWeight: p.fontWeight as string, textAlign: p.align as 'left' | 'center' | 'right' }}>
          {p.content as string}
        </p>
      );
    case 'heading':
      return wrapper(
        <h2 style={{ fontSize: `${p.fontSize}px`, color: p.color as string, fontWeight: 'bold', textAlign: p.align as 'left' | 'center' | 'right' }}>
          {p.content as string}
        </h2>
      );
    case 'image':
      return wrapper(
        <div style={{ borderRadius: `${p.borderRadius}px`, height: `${p.height}px` }} className="w-full overflow-hidden bg-gray-100">
          <img src={p.src as string} alt={p.alt as string} className="w-full h-full object-cover" />
        </div>
      );
    case 'card':
      return wrapper(
        <div style={{ backgroundColor: p.backgroundColor as string }} className={`rounded-xl p-4 ${p.shadow ? 'shadow-md' : ''} border border-gray-100`}>
          <h3 className="font-bold text-gray-900 text-sm mb-1">{p.title as string}</h3>
          <p className="text-xs text-gray-500">{p.subtitle as string}</p>
        </div>
      );
    case 'input':
      return wrapper(
        <div>
          {p.label && <label className="block text-xs font-semibold text-gray-700 mb-1">{p.label as string}</label>}
          <input
            type={p.type as string}
            placeholder={p.placeholder as string}
            className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm bg-gray-50 pointer-events-none"
            readOnly
          />
        </div>
      );
    case 'search':
      return wrapper(
        <div className="relative">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
          <input
            placeholder={p.placeholder as string}
            style={{ backgroundColor: p.backgroundColor as string }}
            className="w-full pl-9 pr-4 py-2.5 rounded-xl text-sm border-none outline-none pointer-events-none"
            readOnly
          />
        </div>
      );
    case 'header':
      return wrapper(
        <div style={{ backgroundColor: p.backgroundColor as string }} className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          {p.showBack && <i className="ri-arrow-left-line text-lg text-gray-700"></i>}
          <span className="font-bold text-gray-900 text-base flex-1 text-center">{p.title as string}</span>
          {p.showMenu && <i className="ri-more-2-fill text-lg text-gray-700"></i>}
        </div>
      );
    case 'tabbar':
      return wrapper(
        <div style={{ color: p.color as string }} className="flex items-center justify-around py-2 border-t border-gray-100 bg-white">
          {(p.tabs as string[]).map((tab, i) => (
            <div key={i} className={`flex flex-col items-center gap-0.5 ${i === (p.activeTab as number) ? '' : 'opacity-40'}`}>
              <i className={['ri-home-5-line', 'ri-search-line', 'ri-shopping-cart-line', 'ri-user-line'][i % 4] + ' text-lg'}></i>
              <span className="text-xs font-medium">{tab}</span>
            </div>
          ))}
        </div>
      );
    case 'list':
      return wrapper(
        <div className="flex items-center gap-3 py-3 border-b border-gray-100">
          {p.showIcon && (
            <div className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 flex-shrink-0">
              <i className={`${p.iconName as string} text-base`} style={{ color: p.iconColor as string }}></i>
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-900 truncate">{p.title as string}</p>
            <p className="text-xs text-gray-500 truncate">{p.subtitle as string}</p>
          </div>
          {p.showArrow && <i className="ri-arrow-right-s-line text-gray-400 flex-shrink-0"></i>}
        </div>
      );
    case 'productcard':
      return wrapper(
        <div className="bg-white rounded-xl overflow-hidden border border-gray-100 shadow-sm">
          <div className="h-28 bg-gray-100 flex items-center justify-center">
            <i className="ri-shopping-bag-line text-3xl text-gray-300"></i>
          </div>
          <div className="p-3">
            {p.badge && <span className="text-xs font-bold text-white bg-red-500 px-2 py-0.5 rounded-full">{p.badge as string}</span>}
            <p className="text-sm font-bold text-gray-900 mt-1">{p.name as string}</p>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-sm font-bold text-teal-600">{p.price as string}</span>
              {p.originalPrice && <span className="text-xs text-gray-400 line-through">{p.originalPrice as string}</span>}
            </div>
            <div className="flex items-center gap-0.5 mt-1">
              {[1,2,3,4,5].map(i => (
                <i key={i} className={`ri-star-${i <= Math.floor(p.rating as number) ? 'fill' : 'line'} text-xs text-yellow-400`}></i>
              ))}
              <span className="text-xs text-gray-400 ml-1">{p.rating as number}</span>
            </div>
          </div>
        </div>
      );
    case 'usercard':
      return wrapper(
        <div className="flex items-center gap-3 p-3 bg-white rounded-xl border border-gray-100 shadow-sm">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-teal-400 to-cyan-500 flex items-center justify-center flex-shrink-0">
            <span className="text-white font-bold text-sm">{(p.name as string).split(' ').map((n: string) => n[0]).join('')}</span>
          </div>
          <div className="flex-1">
            <p className="text-sm font-bold text-gray-900">{p.name as string}</p>
            <p className="text-xs text-gray-500">{p.role as string}</p>
          </div>
          <div className="text-right">
            <p className="text-xs font-bold text-teal-600">{p.followers as string}</p>
            <p className="text-xs text-gray-400">followers</p>
          </div>
        </div>
      );
    case 'notification':
      return wrapper(
        <div className={`flex items-start gap-3 p-3 rounded-xl ${p.unread ? 'bg-teal-50 border border-teal-100' : 'bg-white border border-gray-100'}`}>
          <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${p.unread ? 'bg-teal-500' : 'bg-gray-300'}`}></div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-gray-900">{p.title as string}</p>
            <p className="text-xs text-gray-500 truncate">{p.body as string}</p>
          </div>
          <span className="text-xs text-gray-400 whitespace-nowrap">{p.time as string}</span>
        </div>
      );
    case 'avatar':
      return wrapper(
        <div className="flex items-center justify-center">
          <div
            style={{ backgroundColor: p.backgroundColor as string, width: `${p.size}px`, height: `${p.size}px` }}
            className="rounded-full flex items-center justify-center"
          >
            <span className="text-white font-bold" style={{ fontSize: `${(p.size as number) * 0.35}px` }}>{p.initials as string}</span>
          </div>
        </div>
      );
    case 'badge':
      return wrapper(
        <span style={{ backgroundColor: p.backgroundColor as string, color: p.textColor as string }} className="inline-block px-3 py-1 rounded-full text-xs font-bold">
          {p.text as string}
        </span>
      );
    case 'rating':
      return wrapper(
        <div className="flex items-center gap-1">
          {Array.from({ length: p.max as number }).map((_, i) => (
            <i key={i} className={`ri-star-${i < (p.value as number) ? 'fill' : 'line'} text-xl`} style={{ color: p.color as string }}></i>
          ))}
        </div>
      );
    case 'progress':
      return wrapper(
        <div>
          <div style={{ backgroundColor: p.backgroundColor as string, height: `${p.height}px` }} className="w-full rounded-full overflow-hidden">
            <div style={{ backgroundColor: p.color as string, width: `${p.value}%`, height: '100%' }} className="rounded-full transition-all"></div>
          </div>
        </div>
      );
    case 'toggle':
      return wrapper(
        <div className="flex items-center justify-between py-1">
          <span className="text-sm text-gray-700">{p.label as string}</span>
          <div style={{ backgroundColor: p.enabled ? p.color as string : '#D1D5DB' }} className="w-11 h-6 rounded-full relative transition-colors">
            <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${p.enabled ? 'left-5' : 'left-0.5'}`}></div>
          </div>
        </div>
      );
    case 'checkbox':
      return wrapper(
        <label className="flex items-center gap-2 cursor-pointer">
          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${p.checked ? 'bg-teal-500 border-teal-500' : 'border-gray-300'}`}>
            {p.checked && <i className="ri-check-line text-white text-xs"></i>}
          </div>
          <span className="text-sm text-gray-700">{p.label as string}</span>
        </label>
      );
    case 'divider':
      return wrapper(
        <div style={{ backgroundColor: p.color as string, height: `${p.thickness}px` }} className="w-full my-1"></div>
      );
    case 'spacer':
      return wrapper(<div style={{ height: `${p.height}px` }}></div>);
    case 'map':
      return wrapper(
        <div className="w-full h-36 rounded-xl overflow-hidden bg-gray-100 relative">
          <img
            src="https://public.readdy.ai/ai/img_res/2d1cca96e461d3a4a522223b0c368e13.jpg"
            alt="Map"
            className="w-full h-full object-cover"
          />
          {p.showMarker && (
            <div className="absolute inset-0 flex items-center justify-center">
              <i className="ri-map-pin-2-fill text-3xl text-red-500 drop-shadow-lg"></i>
            </div>
          )}
        </div>
      );
    case 'video':
      return wrapper(
        <div className="w-full rounded-xl overflow-hidden relative bg-gray-900">
          <img src={p.thumbnail as string} alt="Video" className="w-full h-32 object-cover opacity-80" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-12 h-12 bg-white/90 rounded-full flex items-center justify-center shadow-lg">
              <i className="ri-play-fill text-2xl text-gray-900 ml-1"></i>
            </div>
          </div>
          <p className="absolute bottom-2 left-3 text-white text-xs font-semibold drop-shadow">{p.title as string}</p>
          {p.duration && <span className="absolute top-2 right-2 bg-black/70 text-white text-xs px-1.5 py-0.5 rounded">{p.duration as string}</span>}
        </div>
      );
    case 'carousel':
      return wrapper(
        <div className="relative">
          <div className="w-full h-32 bg-gradient-to-r from-teal-400 to-cyan-500 rounded-xl flex items-center justify-center">
            <i className="ri-image-line text-4xl text-white/60"></i>
          </div>
          <div className="flex items-center justify-center gap-1.5 mt-2">
            {Array.from({ length: p.count as number }).map((_, i) => (
              <div key={i} className={`rounded-full transition-all ${i === 0 ? 'w-4 h-1.5 bg-teal-500' : 'w-1.5 h-1.5 bg-gray-300'}`}></div>
            ))}
          </div>
        </div>
      );
    case 'dropdown':
      return wrapper(
        <div className="relative">
          <select className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm bg-gray-50 appearance-none pointer-events-none">
            <option>{p.placeholder as string}</option>
            {(p.options as string[]).map((opt, i) => <option key={i}>{opt}</option>)}
          </select>
          <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
        </div>
      );
    case 'breadcrumb':
      return wrapper(
        <div className="flex items-center gap-1">
          {(p.items as string[]).map((item, i) => (
            <span key={i} className="flex items-center gap-1">
              <span className={`text-xs ${i === (p.items as string[]).length - 1 ? 'text-gray-900 font-semibold' : 'text-gray-400'}`}>{item}</span>
              {i < (p.items as string[]).length - 1 && <i className="ri-arrow-right-s-line text-gray-300 text-xs"></i>}
            </span>
          ))}
        </div>
      );
    case 'row':
      return wrapper(
        <div style={{ gap: `${p.gap}px`, padding: `${p.padding}px` }} className="flex items-center border border-dashed border-gray-200 rounded-lg min-h-[40px]">
          <span className="text-xs text-gray-400 mx-auto">Row Layout</span>
        </div>
      );
    case 'container':
      return wrapper(
        <div style={{ padding: `${p.padding}px`, backgroundColor: p.backgroundColor as string, borderRadius: `${p.borderRadius}px` }} className="border border-dashed border-gray-200 min-h-[48px] flex items-center justify-center">
          <span className="text-xs text-gray-400">Container</span>
        </div>
      );
    case 'countdown':
      return wrapper(
        <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
          <div className="flex items-center justify-center gap-2">
            {p.showDays && (
              <div className="flex flex-col items-center">
                <div style={{ color: p.color as string }} className="text-2xl font-bold">12</div>
                <div className="text-xs text-gray-500">Days</div>
              </div>
            )}
            {p.showHours && (
              <div className="flex flex-col items-center">
                <div style={{ color: p.color as string }} className="text-2xl font-bold">08</div>
                <div className="text-xs text-gray-500">Hours</div>
              </div>
            )}
            {p.showMinutes && (
              <div className="flex flex-col items-center">
                <div style={{ color: p.color as string }} className="text-2xl font-bold">45</div>
                <div className="text-xs text-gray-500">Min</div>
              </div>
            )}
            {p.showSeconds && (
              <div className="flex flex-col items-center">
                <div style={{ color: p.color as string }} className="text-2xl font-bold">23</div>
                <div className="text-xs text-gray-500">Sec</div>
              </div>
            )}
          </div>
        </div>
      );
    case 'notificationbanner':
      return wrapper(
        <div className={`flex items-center gap-3 p-3 rounded-lg ${
          p.type === 'success' ? 'bg-green-50 border border-green-200' :
          p.type === 'warning' ? 'bg-yellow-50 border border-yellow-200' :
          p.type === 'error' ? 'bg-red-50 border border-red-200' :
          'bg-blue-50 border border-blue-200'
        }`}>
          <i className={`${p.icon as string} text-lg ${
            p.type === 'success' ? 'text-green-600' :
            p.type === 'warning' ? 'text-yellow-600' :
            p.type === 'error' ? 'text-red-600' :
            'text-blue-600'
          }`}></i>
          <p className="flex-1 text-sm text-gray-800">{p.message as string}</p>
          {p.dismissible && <i className="ri-close-line text-gray-400 text-sm"></i>}
        </div>
      );
    case 'qrcode':
      return wrapper(
        <div className="flex items-center justify-center p-4">
          <div style={{ 
            width: `${p.size}px`, 
            height: `${p.size}px`,
            backgroundColor: p.backgroundColor as string 
          }} className="rounded-lg border-2 border-gray-200 flex items-center justify-center">
            <div className="grid grid-cols-8 gap-0.5 p-2">
              {Array.from({ length: 64 }).map((_, i) => (
                <div key={i} style={{ backgroundColor: Math.random() > 0.5 ? p.foregroundColor as string : 'transparent' }} className="w-1.5 h-1.5"></div>
              ))}
            </div>
          </div>
        </div>
      );
    case 'chart':
      return wrapper(
        <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
          <p className="text-sm font-bold text-gray-900 mb-3">{p.title as string}</p>
          <div className="flex items-end justify-between gap-1 h-24">
            {(p.data as number[]).map((val, i) => (
              <div key={i} className="flex-1 flex flex-col items-center gap-1">
                <div 
                  style={{ 
                    height: `${(val / 100) * 100}%`,
                    backgroundColor: p.color as string 
                  }} 
                  className="w-full rounded-t"
                ></div>
                <span className="text-xs text-gray-400">{(p.labels as string[])[i]}</span>
              </div>
            ))}
          </div>
        </div>
      );
    case 'loginform':
      return wrapper(
        <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
          <h3 className="text-lg font-bold text-gray-900 mb-4">{p.title as string}</h3>
          <div className="space-y-3">
            <input type="email" placeholder="Email" className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm bg-gray-50 pointer-events-none" readOnly />
            <input type="password" placeholder="Password" className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm bg-gray-50 pointer-events-none" readOnly />
            <button style={{ backgroundColor: p.buttonColor as string }} className="w-full py-2.5 text-white font-semibold rounded-lg text-sm pointer-events-none">
              Sign In
            </button>
            {p.showSocial && (
              <div className="flex gap-2">
                <button className="flex-1 py-2 border border-gray-200 rounded-lg text-xs font-semibold text-gray-700 pointer-events-none">
                  <i className="ri-google-fill mr-1"></i>Google
                </button>
                <button className="flex-1 py-2 border border-gray-200 rounded-lg text-xs font-semibold text-gray-700 pointer-events-none">
                  <i className="ri-facebook-fill mr-1"></i>Facebook
                </button>
              </div>
            )}
          </div>
        </div>
      );
    case 'signupform':
      return wrapper(
        <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
          <h3 className="text-lg font-bold text-gray-900 mb-4">{p.title as string}</h3>
          <div className="space-y-3">
            <input type="text" placeholder="Full Name" className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm bg-gray-50 pointer-events-none" readOnly />
            <input type="email" placeholder="Email" className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm bg-gray-50 pointer-events-none" readOnly />
            <input type="password" placeholder="Password" className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm bg-gray-50 pointer-events-none" readOnly />
            <button style={{ backgroundColor: p.buttonColor as string }} className="w-full py-2.5 text-white font-semibold rounded-lg text-sm pointer-events-none">
              Create Account
            </button>
            {p.showSocial && (
              <div className="flex gap-2">
                <button className="flex-1 py-2 border border-gray-200 rounded-lg text-xs font-semibold text-gray-700 pointer-events-none">
                  <i className="ri-google-fill mr-1"></i>Google
                </button>
                <button className="flex-1 py-2 border border-gray-200 rounded-lg text-xs font-semibold text-gray-700 pointer-events-none">
                  <i className="ri-apple-fill mr-1"></i>Apple
                </button>
              </div>
            )}
          </div>
        </div>
      );
    case 'paymentform':
      return wrapper(
        <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
          <h3 className="text-lg font-bold text-gray-900 mb-4">{p.title as string}</h3>
          <div className="space-y-3">
            <input type="text" placeholder="Card Number" className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm bg-gray-50 pointer-events-none" readOnly />
            <div className="flex gap-2">
              <input type="text" placeholder="MM/YY" className="flex-1 px-3 py-2.5 border border-gray-200 rounded-lg text-sm bg-gray-50 pointer-events-none" readOnly />
              <input type="text" placeholder="CVV" className="flex-1 px-3 py-2.5 border border-gray-200 rounded-lg text-sm bg-gray-50 pointer-events-none" readOnly />
            </div>
            <input type="text" placeholder="Cardholder Name" className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm bg-gray-50 pointer-events-none" readOnly />
            {p.showSavedCards && (
              <div className="flex items-center gap-2 p-2 border border-gray-200 rounded-lg">
                <i className="ri-bank-card-line text-gray-400"></i>
                <span className="text-xs text-gray-600">•••• 4242</span>
              </div>
            )}
            <button style={{ backgroundColor: p.buttonColor as string }} className="w-full py-2.5 text-white font-semibold rounded-lg text-sm pointer-events-none">
              Pay Now
            </button>
          </div>
        </div>
      );
    case 'onboarding':
      return wrapper(
        <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
          <div className="h-32 bg-gradient-to-br from-teal-100 to-cyan-100 rounded-lg flex items-center justify-center mb-3">
            <i className="ri-rocket-line text-4xl text-teal-600"></i>
          </div>
          <h3 className="text-base font-bold text-gray-900 mb-1 text-center">Welcome!</h3>
          <p className="text-xs text-gray-500 text-center mb-4">Get started with your new app</p>
          <div className="flex items-center justify-center gap-1.5 mb-3">
            {Array.from({ length: p.slides as number }).map((_, i) => (
              <div key={i} className={`rounded-full transition-all ${i === (p.currentSlide as number) ? 'w-4 h-1.5' : 'w-1.5 h-1.5'}`} style={{ backgroundColor: i === (p.currentSlide as number) ? p.color as string : '#D1D5DB' }}></div>
            ))}
          </div>
          <div className="flex items-center justify-between">
            {p.showSkip && <button className="text-xs text-gray-400 pointer-events-none">Skip</button>}
            <button style={{ backgroundColor: p.color as string }} className="ml-auto px-4 py-2 text-white text-xs font-semibold rounded-lg pointer-events-none">
              Next
            </button>
          </div>
        </div>
      );
    case 'socialshare':
      return wrapper(
        <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
          <p className="text-sm font-bold text-gray-900 mb-3">{p.title as string}</p>
          <div className="flex items-center justify-center gap-3">
            {(p.platforms as string[]).includes('facebook') && (
              <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center">
                <i className="ri-facebook-fill text-white text-lg"></i>
              </div>
            )}
            {(p.platforms as string[]).includes('twitter') && (
              <div className="w-10 h-10 rounded-full bg-sky-500 flex items-center justify-center">
                <i className="ri-twitter-x-fill text-white text-lg"></i>
              </div>
            )}
            {(p.platforms as string[]).includes('instagram') && (
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                <i className="ri-instagram-fill text-white text-lg"></i>
              </div>
            )}
            {(p.platforms as string[]).includes('whatsapp') && (
              <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center">
                <i className="ri-whatsapp-fill text-white text-lg"></i>
              </div>
            )}
          </div>
        </div>
      );
    case 'imageupload':
      return wrapper(
        <div
          style={{ borderRadius: `${p.borderRadius}px`, height: `${p.height}px` }}
          className="w-full border-2 border-dashed border-teal-300 bg-teal-50 flex flex-col items-center justify-center gap-2 overflow-hidden relative"
        >
          {p.uploadedSrc ? (
            <>
              <img
                src={p.uploadedSrc as string}
                alt="Uploaded"
                className="w-full h-full object-cover"
                style={{ borderRadius: `${p.borderRadius}px` }}
              />
              <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                <span className="text-white text-xs font-semibold">Change Image</span>
              </div>
            </>
          ) : (
            <>
              <div className="w-10 h-10 flex items-center justify-center bg-teal-100 rounded-full">
                <i className="ri-upload-cloud-2-line text-xl text-teal-500"></i>
              </div>
              <p className="text-sm font-semibold text-teal-700">{p.label as string}</p>
              <p className="text-xs text-teal-400">{p.hint as string}</p>
              {p.allowMultiple && (
                <span className="text-xs text-teal-400 bg-teal-100 px-2 py-0.5 rounded-full">Multiple</span>
              )}
            </>
          )}
        </div>
      );
    default:
      return wrapper(
        <div className="p-3 bg-gray-50 rounded-lg border border-dashed border-gray-200 text-center">
          <p className="text-xs text-gray-400">{el.type}</p>
        </div>
      );
  }
}

export default function PhoneCanvas({
  elements,
  selectedId,
  onSelect,
  onDrop,
  onDragOver,
  onReorder,
  appSettings,
  device,
  previewMode = false,
}: PhoneCanvasProps) {
  let dragFromIndex: number | null = null;

  const handleReorderStart = (index: number) => { dragFromIndex = index; };
  const handleReorderOver = (index: number) => {
    if (dragFromIndex !== null && dragFromIndex !== index) {
      onReorder(dragFromIndex, index);
      dragFromIndex = index;
    }
  };
  const handleReorderEnd = () => { dragFromIndex = null; };
  const handleDelete = (id: string) => {
    const event = new CustomEvent('deleteElement', { detail: { id } });
    window.dispatchEvent(event);
  };

  return (
    <div className={`w-[320px] rounded-[2.8rem] p-2.5 shadow-2xl ${device === 'ios' ? 'bg-gray-900' : 'bg-gray-800'}`}>
      <div className="w-full h-full bg-white rounded-[2.3rem] overflow-hidden flex flex-col" style={{ fontFamily: appSettings.fontFamily }}>
        {/* Status Bar */}
        <div className="h-10 bg-white flex items-center justify-between px-6 flex-shrink-0">
          <span className="text-xs font-bold text-gray-900">9:41</span>
          <div className="flex items-center gap-1.5">
            <i className="ri-signal-wifi-fill text-xs text-gray-900"></i>
            <i className="ri-battery-fill text-xs text-gray-900"></i>
          </div>
        </div>

        {/* App Content */}
        <div
          className="flex-1 overflow-y-auto"
          style={{ backgroundColor: appSettings.backgroundColor }}
          onDrop={onDrop}
          onDragOver={onDragOver}
          onClick={() => { if (!previewMode) onSelect(null); }}
        >
          {elements.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center p-6 text-center">
              <div className="w-14 h-14 bg-gray-100 rounded-2xl flex items-center justify-center mb-3">
                <i className="ri-drag-drop-line text-2xl text-gray-400"></i>
              </div>
              <p className="text-sm font-semibold text-gray-500">Drop components here</p>
              <p className="text-xs text-gray-400 mt-1">or use AI to generate your app</p>
            </div>
          ) : (
            <div className="p-3 space-y-2">
              {elements.map((el, index) =>
                renderElement(
                  el,
                  selectedId === el.id,
                  onSelect,
                  handleDelete,
                  handleReorderStart,
                  handleReorderOver,
                  handleReorderEnd,
                  index,
                  previewMode
                )
              )}
            </div>
          )}
        </div>

        {/* Home Indicator */}
        <div className="h-6 bg-white flex items-center justify-center flex-shrink-0">
          <div className="w-24 h-1 bg-gray-300 rounded-full"></div>
        </div>
      </div>
    </div>
  );
}